<template>
    <div v-if="show" class="item-modal bg-light">
        <div class="modal-content">
            <div class="container">
                <span @click="closeModal" role="button">
                    <small><i class="fa-solid fa-chevron-left me-2"></i>{{ $t('common.back') }}</small>
                </span>
                <h2 class="my-3">{{ $t('modal.addTimeEntry') }}</h2>

                <div class="mb-3">
                    <label for="profile" class="form-label small">{{ $t('modal.workType') }}</label>
                    <select v-model="profile" class="form-control bg-light">
                        <option v-for="profile in profiles" :key="profile.id" :value="profile.id">{{ profile.name }}
                        </option>
                    </select>
                </div>
                <div class="mb-3">
                    <label for="date" class="form-label small">{{ $t('task.date') }}</label>
                    <VueDatePicker v-model="date" :format="dateFormat" auto-apply input-class-name="bg-light"
                        :locale="locale" :clearable="false" required></VueDatePicker>
                </div>
                <div class="mb-3">
                    <label for="startTime" class="form-label small">{{ $t('modal.start') }}</label>
                    <VueDatePicker v-model="startTime" :format="timeFormat" :time-picker="true"
                        input-class-name="bg-light" :locale="locale" :cancel-text="$t('common.cancel')" :select-text="$t('common.select')" :clearable="false"
                        required></VueDatePicker>
                </div>
                <div class="mb-3">
                    <label for="duration" class="form-label small">{{ $t('modal.durationHours') }}</label>
                    <input id="duration" class="form-control bg-light" v-model="duration" :placeholder="$t('modal.duration')"
                        type="text" inputmode="decimal" pattern="[0-9]+([,][0-9]{1,2})?"
                        @input="validateAmountInput('duration', $event.target.value)" />
                </div>
                <div class="mb-3">
                    <label for="description" class="form-label small">{{ $t('browse.description') }}</label>
                    <textarea id="description" class="form-control bg-light" v-model="description" :placeholder="$t('taskDetail.descriptionPlaceholder')"
                        rows="3"></textarea>
                </div>
                <div class="row mt-3">
                    <div class="col">
                        <button @click="closeModal" :disabled="actionInProgress"
                            class="btn border-1 border-primary text-primary w-100">{{ $t('common.cancel') }}</button>
                    </div>
                    <div class="col">
                        <button @click="saveEvent" class="btn btn-primary w-100" :disabled="actionInProgress">{{ $t('common.save') }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from '@/services/axios';
import VueDatePicker from '@vuepic/vue-datepicker';
import { useI18n } from 'vue-i18n';
import { toApiDateTime } from '@/utils/datetime';

export default {
    components: {
        VueDatePicker
    },
    setup() {
        const { locale } = useI18n();
        return { locale };
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        event: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            profiles: [],
            profile: null,
            date: null,
            startTime: null,
            duration: null,
            description: '',
            dateFormat: 'dd.MM.yyyy',
            timeFormat: 'HH:mm',
            isEditMode: false,
            // Held only while the request is in flight, so a slow network
            // cannot turn one click into two records.
            actionInProgress: false,
        };
    },
    watch: {
        event: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.isEditMode = true;
                    this.profile = newVal.profile_id;
                    this.date = newVal.start_time.slice(0, 10);
                    this.startTime = {
                        hours: newVal.start_time.slice(11, 13),
                        minutes: newVal.start_time.slice(14, 16)
                    };
                    this.duration = newVal.duration;
                    this.description = newVal.description;
                } else {
                    this.isEditMode = false;
                    this.resetForm();
                }
            }
        }
    },
    methods: {
        closeModal() {
            this.$emit('close');
        },
        async fetchProfiles() {
            try {
                const response = await axios.get('/profiles?for_activity_resources_only=1&not_active=0');
                this.profiles = response.data.data;
            } catch (error) {
                console.error('Error fetching profiles:', error);
            }
        },
        async saveEvent() {
            if (!this.profile) {
                this.$root.$refs.globalAlert.showAlert(this.$t('modal.workTypeRequired'), 'danger');
                return;
            }

            if (!this.date) {
                this.$root.$refs.globalAlert.showAlert(this.$t('modal.dateRequired'), 'danger');
                return;
            }

            if (!this.startTime || this.startTime.hours === undefined || this.startTime.minutes === undefined) {
                this.$root.$refs.globalAlert.showAlert(this.$t('modal.startTimeRequired'), 'danger');
                return;
            }

            if (!this.duration) {
                this.$root.$refs.globalAlert.showAlert(this.$t('modal.durationRequired'), 'danger');
                return;
            }
            // After the validations, so a bailed-out submit does not leave the
            // button stuck disabled.
            if (this.actionInProgress) return;
            this.actionInProgress = true;
            try {
                const d = new Date(this.date);
                d.setHours(this.startTime.hours, this.startTime.minutes, 0, 0);
                const formattedStartDateTime = toApiDateTime(d);
                const event = {
                    profile_id: this.profile,
                    start_time: formattedStartDateTime,
                    duration: this.duration,
                    description: this.description,
                };

                if (this.isEditMode) {
                    await axios.put(`/events/resources/${this.event.resource_id}`, event);
                } else {
                    await axios.post('/events/resources/', event);
                }
                this.$emit('event-saved');
                this.closeModal();
                this.resetForm();
            } catch (error) {
                console.error('Error saving event:', error);
            } finally {
                this.actionInProgress = false;
            }
        },
        resetForm() {
            this.profile = null;
            this.date = null;
            this.startTime = null;
            this.duration = null;
            this.description = '';
        },
        validateAmountInput(propertyName, inputValue) {
            let parsedValue = inputValue.replace(/,/g, '.');
            this[propertyName] = parsedValue;
        },
    },
    mounted() {
        this.fetchProfiles();
    }
};
</script>

<style scoped>
.item-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 5px;
    z-index: 1031;
    display: flex;
    justify-content: center;
    align-items: center;
}

.modal-content {
    height: 100%;
    width: 100%;
    padding: 20px;
    overflow-y: auto;
}
</style>